import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Drawer, Dropdown } from 'antd';
import {
  CloseOutlined, FileTextOutlined,
  UserOutlined, LoginOutlined, LogoutOutlined, DashboardOutlined,
  HomeOutlined, HomeFilled,
  AppstoreOutlined,
  PictureOutlined, PictureFilled,
  MessageOutlined, MessageFilled,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';

const mobileTabs = [
  { key: '/', icon: <HomeOutlined />, activeIcon: <HomeFilled />, label: 'Accueil' },
  { key: '/services', icon: <AppstoreOutlined />, activeIcon: <AppstoreOutlined />, label: 'Services' },
  { key: '/demander-devis', icon: <FileTextOutlined />, activeIcon: <FileTextOutlined />, label: 'Devis' },
  { key: '/galerie', icon: <PictureOutlined />, activeIcon: <PictureFilled />, label: 'Galerie' },
  { key: '/contact', icon: <MessageOutlined />, activeIcon: <MessageFilled />, label: 'Contact' },
  { key: '/login', icon: <LoginOutlined />, activeIcon: <LoginOutlined />, label: 'Connexion' },
];

const servicesLinks = [
  { to: '/hadj-oumra', label: 'Hadj & Oumra' },
  { to: '/services/location-vehicules', label: 'Location véhicules' },
  { to: '/services/navette-aibd', label: 'Navette AIBD' },
  { to: '/services/assurance', label: 'Assurance' },
  { to: '/services/conseils', label: 'Conseils' },
];

const Header = () => {
  const [visible, setVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const preloadLogin = () => import('../../pages/Login');

  useEffect(() => {
    setVisible(false);
    setServicesOpen(false);
  }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { key: '/', label: 'Accueil', to: '/' },
    { key: '/a-propos', label: 'À propos', to: '/a-propos' },
    {
      key: 'services',
      label: 'Nos Services',
      children: servicesLinks.map(s => ({ key: s.to, label: s.label })),
    },
    { key: '/galerie', label: 'Galerie', to: '/galerie' },
    { key: '/actualites', label: 'Actualités', to: '/actualites' },
    { key: '/contact', label: 'Contact', to: '/contact' },
  ];

  const isActive = (item) => {
    if (item.to === '/') return location.pathname === '/';
    return location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
  };

  const isParentActive = (children) =>
    children.some((c) => location.pathname === c.key || location.pathname.startsWith(`${c.key}/`));

  const isMobileTabActive = (tab) => {
    if (tab.key === '/') return location.pathname === '/';
    if (tab.key === '/demander-devis') return location.pathname === '/demander-devis';
    if (tab.key === '/services') return location.pathname.startsWith('/services') || location.pathname === '/hadj-oumra';
    if (tab.key === '/login') return location.pathname === '/login';
    if (tab.key === '/galerie') return location.pathname === '/galerie' || location.pathname.startsWith('/galerie/');
    if (tab.key === '/contact') return location.pathname === '/contact' || location.pathname.startsWith('/contact/');
    return location.pathname === tab.key || location.pathname.startsWith(`${tab.key}/`);
  };

  const userMenuItems = [
    ...(isAdmin ? [{ key: 'admin', icon: <DashboardOutlined />, label: 'Administration', onClick: () => navigate('/admin') }] : []),
    ...(!isAdmin ? [{ key: 'devis', icon: <FileTextOutlined />, label: 'Demander un devis', onClick: () => navigate('/demander-devis') }] : []),
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Déconnexion', onClick: () => logout() },
  ];

  return (
    <>
      <header className={`header-main${scrolled ? ' scrolled' : ''}`} style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
      }}>
        <div style={{
          maxWidth: '1280px', margin: '0 auto', padding: '0 24px',
          height: '76px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '16px',
        }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <img
              src="/img/logo-new.png"
              alt="Ndiouroul Voyages"
              style={{ height: '62px', width: 'auto', maxWidth: '100%', objectFit: 'contain' }}
            />
          </Link>

          <nav className="desktop-menu" style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            {navItems.map(item => (
              !item.children ? (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`nav-link${isActive(item) ? ' active' : ''}`}
                >
                  {item.label}
                </Link>
              ) : (
                <div key={item.key} className="dropdown-trigger" style={{ position: 'relative' }}>
                  <span className={`nav-link${isParentActive(item.children) ? ' active' : ''}`}
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    {item.label}
                    <span style={{ fontSize: '9px', opacity: 0.7 }}>▼</span>
                  </span>
                  <div className="dropdown-menu">
                    {item.children.map((child, i) =>
                      child.type === 'divider' ? (
                        <div key={i} style={{
                          height: '1px', background: 'var(--border-soft)', margin: '6px 10px',
                        }} />
                      ) : (
                        <Link
                          key={child.key}
                          to={child.key}
                          className={`dropdown-link${isActive(child) ? ' active' : ''}`}
                        >
                          {child.label}
                        </Link>
                      )
                    )}
                  </div>
                </div>
              )
            ))}
          </nav>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            {!isAdmin && (
              <Link to="/demander-devis">
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  className="btn-gold"
                  style={{ height: '42px', borderRadius: '11px', fontSize: '14px' }}
                >
                  Devis gratuit
                </Button>
              </Link>
            )}
            {user ? (
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <Button
                  type="text"
                  icon={<UserOutlined />}
                  style={{
                    height: '42px', borderRadius: '11px', fontWeight: '600', color: 'var(--primary-color)',
                    background: 'rgba(30,58,95,0.06)',
                  }}
                >
                  {user.name}
                </Button>
              </Dropdown>
            ) : (
              <Button
                type="default"
                icon={<LoginOutlined />}
                onMouseEnter={preloadLogin}
                onFocus={preloadLogin}
                style={{
                  height: '42px', borderRadius: '11px', fontWeight: '600', color: 'var(--primary-color)',
                  borderColor: 'rgba(30,58,95,0.4)',
                }}
              >
                <Link to="/login" style={{ color: 'var(--primary-color)' }}>Connexion</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Services bottom sheet for mobile */}
      <Drawer
        placement="bottom"
        height="auto"
        onClose={() => setServicesOpen(false)}
        open={servicesOpen}
        closable={false}
        styles={{ body: { padding: '12px 8px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {servicesLinks.map(s => (
            <Link
              key={s.to}
              to={s.to}
              onClick={() => { setServicesOpen(false); }}
              style={{
                display: 'block', padding: '12px 16px', borderRadius: '10px',
                fontSize: '15px',
                fontWeight: (location.pathname === s.to || location.pathname.startsWith(s.to + '/')) ? 600 : 400,
                color: (location.pathname === s.to || location.pathname.startsWith(s.to + '/'))
                  ? 'var(--secondary-dark)' : 'var(--text-dark)',
                background: (location.pathname === s.to || location.pathname.startsWith(s.to + '/'))
                  ? 'rgba(0,184,148,0.08)' : 'transparent',
              }}
            >
              {s.label}
            </Link>
          ))}
        </div>
      </Drawer>

      {/* Instagram-style bottom tab bar */}
      <nav className="mobile-bottom-bar">
        {mobileTabs.map(tab => {
          const active = isMobileTabActive(tab);
          if (tab.key === '/services') {
            return (
              <button
                key={tab.key}
                className={`mobile-tab${active ? ' active' : ''}`}
                onClick={() => setServicesOpen(v => !v)}
              >
                <span className="mobile-tab-icon">{active ? tab.activeIcon : tab.icon}</span>
                <span className="mobile-tab-label">{tab.label}</span>
              </button>
            );
          }
          if (tab.key === '/login') {
            if (user) {
              return (
                <button
                  key={tab.key}
                  className={`mobile-tab${location.pathname.startsWith('/admin') ? ' active' : ''}`}
                  onClick={() => navigate(isAdmin ? '/admin' : '/demander-devis')}
                >
                  <span className="mobile-tab-icon"><UserOutlined /></span>
                  <span className="mobile-tab-label">{isAdmin ? 'Admin' : 'Profil'}</span>
                </button>
              );
            }
            return (
              <Link
                key={tab.key}
                to={tab.key}
                className={`mobile-tab${active ? ' active' : ''}`}
              >
                <span className="mobile-tab-icon">{active ? tab.activeIcon : tab.icon}</span>
                <span className="mobile-tab-label">Connexion</span>
              </Link>
            );
          }
          return (
            <Link
              key={tab.key}
              to={tab.key}
              className={`mobile-tab${active ? ' active' : ''}`}
            >
              <span className="mobile-tab-icon">{active ? tab.activeIcon : tab.icon}</span>
              <span className="mobile-tab-label">{tab.label}</span>
            </Link>
          );
        })}
      </nav>

      <style>{`
        .dropdown-trigger:hover .dropdown-menu {
          opacity: 1 !important;
          visibility: visible !important;
          transform: translateY(0) !important;
        }
        .dropdown-menu {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 18px 50px rgba(11, 29, 51, 0.16);
          padding: 8px;
          min-width: 240px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(12px);
          transition: all 0.25s ease;
          z-index: 100;
          border: 1px solid var(--border-soft);
        }
        .dropdown-link {
          display: flex;
          align-items: center;
          padding: 10px 14px;
          border-radius: 9px;
          font-size: 13.5px;
          color: var(--text-body);
          transition: all 0.2s;
          gap: 8px;
        }
        .dropdown-link::before {
          content: '';
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--secondary-color);
          opacity: 0;
          transition: all 0.2s;
        }
        .dropdown-link:hover {
          background: rgba(0, 184, 148, 0.08);
          color: var(--secondary-dark);
          padding-left: 18px;
        }
        .dropdown-link:hover::before,
        .dropdown-link.active::before {
          opacity: 1;
        }
        .dropdown-link.active {
          color: var(--secondary-dark);
          font-weight: 600;
        }

        .mobile-bottom-bar {
          display: none;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background: #ffffff;
          border-top: 0.5px solid rgba(0, 0, 0, 0.1);
          box-shadow: none;
          padding: 0 0 env(safe-area-inset-bottom, 0px);
          justify-content: space-around;
          align-items: center;
          height: 50px;
        }
        .mobile-tab {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1px;
          flex: 1;
          height: 100%;
          padding: 6px 0 4px;
          text-decoration: none;
          color: #262626;
          transition: color 0.15s ease;
          border: none;
          background: none;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }
        .mobile-tab:hover {
          color: #C6A030;
        }
        .mobile-tab:active {
          opacity: 0.6;
        }
        .mobile-tab.active {
          color: #C6A030;
        }
        .mobile-tab-icon {
          font-size: 24px;
          line-height: 1;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .mobile-tab-label {
          font-size: 10px;
          font-weight: 400;
          line-height: 1;
        }
        .mobile-tab.active .mobile-tab-label {
          font-weight: 600;
        }

        .mobile-menu-btn { display: none !important; }

        @media (max-width: 992px) {
          .header-main { display: none !important; }
          .mobile-bottom-bar { display: flex !important; }
          body { padding-bottom: 50px !important; }
        }
      `}</style>
    </>
  );
};

export default Header;
